import { Spec } from '../core/proto/common.js';
import { ActionId } from '../core/proto_utils/action_id.js';
import { Player } from '../core/player.js';
import { TypedEvent } from '../core/typed_event.js';
import { t } from '../core/i18n/index.js';

import {
	Mage_Rotation_PrimaryFireSpell as PrimaryFireSpell,
	Mage_Options_ArmorType as ArmorType,
} from '../core/proto/mage.js';

import * as InputHelpers from '../core/components/input_helpers.js';

// Configuration for spec-specific UI elements on the settings tab.
// These don't need to be in a separate file but it keeps things cleaner.

export const Armor = InputHelpers.makeSpecOptionsEnumIconInput<Spec.SpecMage, ArmorType>({
	fieldName: 'armor',
	values: [
		{ value: ArmorType.NoArmor, tooltip: t('mage.noArmor') },
		{ actionId: ActionId.fromSpellId(43024), value: ArmorType.MageArmor },
		{ actionId: ActionId.fromSpellId(43046), value: ArmorType.MoltenArmor },
	],
});

export const WaterElementalDisobeyChance = InputHelpers.makeSpecOptionsNumberInput<Spec.SpecMage>({
	fieldName: 'waterElementalDisobeyChance',
	percent: true,
	label: t('mage.waterEleDisobeyPercent'),
	labelTooltip: t('mage.waterEleDisobeyPercentTooltip'),
	changeEmitter: (player: Player<Spec.SpecMage>) => TypedEvent.onAny([player.specOptionsChangeEmitter, player.talentsChangeEmitter]),
	showWhen: (player: Player<Spec.SpecMage>) => player.getTalents().summonWaterElemental,
});

export const FocusMagicUptime = InputHelpers.makeSpecOptionsNumberInput<Spec.SpecMage>({
	fieldName: 'focusMagicPercentUptime',
	label: t('mage.focusMagicPercentUptime'),
	labelTooltip: t('mage.focusMagicPercentUptimeTooltip'),
	extraCssClasses: ['within-raid-sim-hide'],
});

export const MageRotationConfig = {
	inputs: [
		// ********************************************************
		//                       FIRE INPUTS
		// ********************************************************
		InputHelpers.makeRotationEnumInput<Spec.SpecMage, PrimaryFireSpell>({
			fieldName: 'primaryFireSpell',
			label: t('mage.primarySpell'),
			values: [
				{ name: t('mage.fireball'), value: PrimaryFireSpell.Fireball },
				{ name: t('mage.frostfireBolt'), value: PrimaryFireSpell.FrostfireBolt },
				{ name: t('mage.scorch'), value: PrimaryFireSpell.Scorch },
			],
			showWhen: (player: Player<Spec.SpecMage>) => player.getTalentTree() == 1,
			changeEmitter: (player: Player<Spec.SpecMage>) => TypedEvent.onAny([player.rotationChangeEmitter, player.talentsChangeEmitter]),
		}),
		// ********************************************************
		//                       FROST INPUTS
		// ********************************************************
		InputHelpers.makeRotationBooleanInput<Spec.SpecMage>({
			fieldName: 'useIceLance',
			label: t('mage.useIceLance'),
			labelTooltip: t('mage.useIceLanceTooltip'),
			showWhen: (player: Player<Spec.SpecMage>) => player.getTalentTree() == 2,
			changeEmitter: (player: Player<Spec.SpecMage>) => TypedEvent.onAny([player.rotationChangeEmitter, player.talentsChangeEmitter]),
		}),
		// ********************************************************
		//                      ARCANE INPUTS
		// ********************************************************
		InputHelpers.makeRotationNumberInput<Spec.SpecMage>({
			fieldName: 'only3ArcaneBlastStacksBelowManaPercent',
			percent: true,
			label: t('mage.stackArcaneBlastTo3BelowManaPercent'),
			labelTooltip: t('mage.stackArcaneBlastTo3BelowManaPercentTooltip'),
			showWhen: (player: Player<Spec.SpecMage>) => player.getTalentTree() == 0,
			changeEmitter: (player: Player<Spec.SpecMage>) => TypedEvent.onAny([player.rotationChangeEmitter, player.talentsChangeEmitter]),
		}),
		InputHelpers.makeRotationNumberInput<Spec.SpecMage>({
			fieldName: 'blastWithoutMissileBarrageAboveManaPercent',
			percent: true,
			label: t('mage.abWithoutMissileBarrageAboveManaPercent'),
			labelTooltip: t('mage.abWithoutMissileBarrageAboveManaPercentTooltip'),
			showWhen: (player: Player<Spec.SpecMage>) => player.getTalentTree() == 0,
			changeEmitter: (player: Player<Spec.SpecMage>) => TypedEvent.onAny([player.rotationChangeEmitter, player.talentsChangeEmitter]),
		}),
		InputHelpers.makeRotationNumberInput<Spec.SpecMage>({
			fieldName: 'missileBarrageBelowManaPercent',
			percent: true,
			label: t('mage.useMissileBarrageASAPBelowManaPercent'),
			labelTooltip: t('mage.useMissileBarrageASAPBelowManaPercentTooltip'),
			showWhen: (player: Player<Spec.SpecMage>) => player.getTalentTree() == 0,
			changeEmitter: (player: Player<Spec.SpecMage>) => TypedEvent.onAny([player.rotationChangeEmitter, player.talentsChangeEmitter]),
		}),
		InputHelpers.makeRotationBooleanInput<Spec.SpecMage>({
			fieldName: 'useArcaneBarrage',
			label: t('mage.useArcaneBarrage'),
			labelTooltip: t('mage.useArcaneBarrageTooltip'),
			enableWhen: (player: Player<Spec.SpecMage>) => player.getTalents().arcaneBarrage,
			showWhen: (player: Player<Spec.SpecMage>) => player.getTalentTree() == 0,
			changeEmitter: (player: Player<Spec.SpecMage>) => TypedEvent.onAny([player.rotationChangeEmitter, player.talentsChangeEmitter]),
		}),

		InputHelpers.makeRotationBooleanInput<Spec.SpecMage>({
			fieldName: 'maintainImprovedScorch',
			label: t('mage.maintainImpScorch'),
			labelTooltip: t('mage.maintainImpScorchTooltip'),
			showWhen: (player: Player<Spec.SpecMage>) => player.getTalents().improvedScorch > 0,
			changeEmitter: (player: Player<Spec.SpecMage>) => TypedEvent.onAny([player.rotationChangeEmitter, player.talentsChangeEmitter]),
		}),
	],
};
